import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import numeral from 'numeral';
import { DailyNavClosedTransactionGroup } from 'api';
import { formatProviderCode, shortDate } from 'utilities';
import {
  CLOSED_TRANSACTION_WINDOWS,
  ClosedTransactionFilters,
  closedTransactionGroupKey,
  ClosedTransactionSortKey,
  ClosedTransactionStats,
  closedTransactionStats,
  ClosedTransactionWindow,
  closedTransactionWindowRange,
  DEFAULT_CLOSED_TRANSACTION_WINDOW,
  EMPTY_CLOSED_TRANSACTION_FILTERS,
  filterGroupsToColumns,
  filterGroupsToWindow,
  formatClosedTransactionSources,
  formatClosingDate,
  providerFilterOptions,
  securityTypeFilterOptions,
  sortGroups,
} from './closedTransactions.utils';
import { ClosedTransactionsDialogProps } from './ClosedTransactionsDialog.props';

// Same two decimals as every price in the grid behind this dialog.
const formatPrice = (value: number | string): string => `$${numeral(Number(value)).format('0,0.00')}`;

const EM_DASH = '—';

// The rows scroll inside this, so the Volume Weighted Average Price below stays
// on screen no matter how many trades the selected window holds.
const TABLE_MAX_HEIGHT = 420;

interface ColumnDefinition {
  key: ClosedTransactionSortKey;
  label: string;
  align: 'left' | 'right';
}

const COLUMNS: ColumnDefinition[] = [
  { key: 'date', label: 'Closing date', align: 'left' },
  { key: 'securityType', label: 'Security type', align: 'left' },
  { key: 'structure', label: 'Structure', align: 'left' },
  { key: 'price', label: 'Price / share', align: 'right' },
  { key: 'size', label: 'Est. transaction size', align: 'right' },
  { key: 'shares', label: 'Est. shares', align: 'right' },
  { key: 'sources', label: 'Sources', align: 'left' },
];

const windowLabel = (windowKey: ClosedTransactionWindow): string => {
  switch (windowKey) {
    case 'YTD':
      return 'year to date';
    case '3M':
      return 'trailing 3 months';
    case '6M':
      return 'trailing 6 months';
    case '1Y':
      return 'trailing 1 year';
    case '2Y':
      return 'trailing 2 years';
    default:
      return windowKey;
  }
};

// Which of the three narrowings emptied the table, so the reader knows whether
// to widen the window, clear a filter, or stop looking.
const emptyReason = (total: number, inWindow: number, windowKey: ClosedTransactionWindow): string => {
  if (total === 0) {
    return 'No closed secondary transactions are available for this row.';
  }
  if (inWindow === 0) {
    return `No closed secondary transactions in the ${windowLabel(windowKey)}.`;
  }
  return 'No closed secondary transactions match the column filters.';
};

// Every definition names the same population first, because that is the part a
// reader cannot see: the window, the column filters and the checkboxes all
// narrow it.
const populationSentence = (stats: ClosedTransactionStats, windowKey: ClosedTransactionWindow): string => {
  const rows = stats.priceCount === 1 ? '1 included trade group' : `${stats.priceCount} included trade groups`;
  return `${rows} in the ${windowLabel(windowKey)}. Filtered-out and unchecked rows are excluded.`;
};

const statDefinitions = (
  stats: ClosedTransactionStats,
  windowKey: ClosedTransactionWindow
): Record<'low' | 'average' | 'median' | 'high', string> => {
  const population = populationSentence(stats, windowKey);
  const missing = stats.unweightedCount
    ? ` ${stats.unweightedCount} row(s) reported no volume and are excluded from this figure only — they still count toward Low, Median and High.`
    : '';
  return {
    low: `Lowest price per share across the ${population}`,
    high: `Highest price per share across the ${population}`,
    median: `Middle price per share across the ${population} Every row counts once, regardless of size.`,
    average: `Volume weighted average price: the sum of price × volume divided by total volume, across the ${population} Volume is the measured size when a provider reported one, otherwise the midpoint of the min–max range the providers bounded it to.${missing}`,
  };
};

interface ColumnFilterProps {
  label: string;
  options: string[];
  hidden: Set<string>;
  onToggle: (value: string) => void;
  onClear: () => void;
  formatOption?: (value: string) => string;
  testId: string;
}

// A checklist of the values actually present in the window, so the only way to
// filter to nothing is deliberate.
const ColumnFilter: FC<ColumnFilterProps> = ({
  label,
  options,
  hidden,
  onToggle,
  onClear,
  formatOption,
  testId,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = hidden.size > 0;

  return (
    <>
      <Tooltip arrow title={active ? `${label} filtered` : `Filter ${label.toLowerCase()}`}>
        <IconButton
          size="small"
          disabled={options.length === 0}
          onClick={event => setAnchor(event.currentTarget)}
          data-testid={testId}
          data-filter-active={active ? 'true' : undefined}
          aria-label={`Filter ${label.toLowerCase()}`}
          sx={{ ml: 0.25, p: 0.25, color: active ? 'primary.main' : 'text.secondary' }}>
          {active ? <FilterAltIcon fontSize="inherit" /> : <FilterAltOutlinedIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {options.map(option => (
          <MenuItem
            key={option}
            dense
            onClick={() => onToggle(option)}
            data-testid={`${testId}-option-${option}`}>
            <Checkbox size="small" checked={!hidden.has(option)} sx={{ p: 0, mr: 1 }} />
            <Typography variant="body2">{formatOption ? formatOption(option) : option}</Typography>
          </MenuItem>
        ))}
        <MenuItem dense disabled={!active} onClick={onClear} data-testid={`${testId}-clear`}>
          <Typography variant="body2" fontWeight={700}>
            Show all
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

const StatRow: FC<{ label: string; value: number | null; definition: string; testId: string }> = ({
  label,
  value,
  definition,
  testId,
}) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Typography variant="body2" fontWeight={700} sx={{ minWidth: '4.5rem' }}>
      {label}
    </Typography>
    <Typography variant="body2" data-testid={testId} sx={{ minWidth: '5rem', textAlign: 'right' }}>
      {value === null ? EM_DASH : formatPrice(value)}
    </Typography>
    <Tooltip arrow placement="right" title={definition}>
      <InfoOutlinedIcon
        fontSize="small"
        data-testid={`${testId}-info`}
        aria-label={`${label} definition`}
        sx={{ color: 'text.secondary', cursor: 'help' }}
      />
    </Tooltip>
  </Box>
);

const ClosedTransactionsDialog: FC<ClosedTransactionsDialogProps> = ({ row, open, onClose }) => {
  const groups = useMemo(() => row?.record.secondary_breakdown?.closed_transactions ?? [], [row]);
  const reportDate = row?.record.date ?? '';

  const [windowKey, setWindowKey] = useState<ClosedTransactionWindow>(DEFAULT_CLOSED_TRANSACTION_WINDOW);
  const [sortKey, setSortKey] = useState<ClosedTransactionSortKey>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Only exclusions are tracked, so a row the backend adds on a refetch arrives
  // included. Dialog-local and display-only: nothing here is ever saved.
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<ClosedTransactionFilters>(EMPTY_CLOSED_TRANSACTION_FILTERS);

  // A fresh open is a fresh question. Carrying one company's exclusions into the
  // next company's dialog would silently skew its averages.
  useEffect(() => {
    if (!open) {
      return;
    }
    setWindowKey(DEFAULT_CLOSED_TRANSACTION_WINDOW);
    setSortKey('date');
    setSortDirection('desc');
    setExcludedKeys(new Set());
    setFilters(EMPTY_CLOSED_TRANSACTION_FILTERS);
  }, [open, row?.companyId]);

  const range = useMemo(() => closedTransactionWindowRange(windowKey, reportDate), [windowKey, reportDate]);

  // The window first, so the filter menus only ever offer values a reader can
  // currently see.
  const windowGroups = useMemo(() => filterGroupsToWindow(groups, range), [groups, range]);

  const visibleGroups = useMemo(
    () => sortGroups(filterGroupsToColumns(windowGroups, filters), sortKey, sortDirection),
    [windowGroups, filters, sortKey, sortDirection]
  );

  const securityTypeOptions = useMemo(() => securityTypeFilterOptions(windowGroups), [windowGroups]);
  const providerOptions = useMemo(() => providerFilterOptions(windowGroups), [windowGroups]);

  const toggleHidden = useCallback((field: keyof ClosedTransactionFilters, value: string) => {
    setFilters(current => {
      const next = new Set(current[field]);
      if (!next.delete(value)) {
        next.add(value);
      }
      return { ...current, [field]: next };
    });
  }, []);

  const clearHidden = useCallback((field: keyof ClosedTransactionFilters) => {
    setFilters(current => ({ ...current, [field]: new Set<string>() }));
  }, []);

  // Only the two text columns are filterable: the rest are continuous, where a
  // window or a sort answers the question a checklist would.
  const columnFilter = (key: ClosedTransactionSortKey) => {
    if (key === 'securityType') {
      return (
        <ColumnFilter
          label="Security type"
          options={securityTypeOptions}
          hidden={filters.hiddenSecurityTypes}
          onToggle={value => toggleHidden('hiddenSecurityTypes', value)}
          onClear={() => clearHidden('hiddenSecurityTypes')}
          testId="closed-transactions-filter-securityType"
        />
      );
    }
    if (key === 'sources') {
      return (
        <ColumnFilter
          label="Sources"
          options={providerOptions}
          hidden={filters.hiddenProviders}
          onToggle={value => toggleHidden('hiddenProviders', value)}
          onClear={() => clearHidden('hiddenProviders')}
          formatOption={formatProviderCode}
          testId="closed-transactions-filter-sources"
        />
      );
    }
    return null;
  };

  const stats = useMemo(
    () => closedTransactionStats(visibleGroups.filter(group => !excludedKeys.has(closedTransactionGroupKey(group)))),
    [visibleGroups, excludedKeys]
  );

  const definitions = useMemo(() => statDefinitions(stats, windowKey), [stats, windowKey]);

  const toggleExcluded = useCallback((group: DailyNavClosedTransactionGroup) => {
    const key = closedTransactionGroupKey(group);
    setExcludedKeys(current => {
      const next = new Set(current);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  // Dates start newest-first and text starts A–Z, which is what a reader expects
  // of each; re-clicking the active column flips it.
  const requestSort = useCallback(
    (key: ClosedTransactionSortKey) => {
      if (key === sortKey) {
        setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'));
        return;
      }
      setSortKey(key);
      setSortDirection(key === 'date' || key === 'price' || key === 'size' || key === 'shares' ? 'desc' : 'asc');
    },
    [sortKey]
  );

  const windowCaption = range ? `${shortDate(range.startDate)} – ${shortDate(range.endDate)}` : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      id="closed-transactions-dialog"
      data-testid="closed-transactions-dialog">
      <DialogTitle>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h6">
              Closed Transactions{row?.companyName ? ` — ${row.companyName}` : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Trades after the last valuation are highlighted.
              {windowCaption ? ` Showing ${windowCaption}.` : ''}
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={windowKey}
            onChange={(_event, value: ClosedTransactionWindow | null) => value && setWindowKey(value)}
            data-testid="closed-transactions-window"
            aria-label="Closed transaction window">
            {CLOSED_TRANSACTION_WINDOWS.map(option => (
              <ToggleButton key={option} value={option} id={`closed-transactions-window-${option}`}>
                {option}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </DialogTitle>
      {/* Capped and scrollable, with the averages below it rather than inside:
          however long the selected window is, the footer stays on screen. */}
      <DialogContent
        sx={{ maxHeight: TABLE_MAX_HEIGHT }}
        data-testid="closed-transactions-scroll">
        {visibleGroups.length === 0 && (
          <Alert
            severity="info"
            data-testid="closed-transactions-empty"
            // The filter menus live in a header that is gone with the rows, so
            // the way back out has to travel with the message.
            action={
              windowGroups.length > 0 ? (
                <Button
                  size="small"
                  onClick={() => setFilters(EMPTY_CLOSED_TRANSACTION_FILTERS)}
                  data-testid="closed-transactions-clear-filters">
                  Show all
                </Button>
              ) : undefined
            }>
            {emptyReason(groups.length, windowGroups.length, windowKey)}
          </Alert>
        )}
        {visibleGroups.length > 0 && (
          <Table
            size="small"
            stickyHeader
            data-testid="closed-transactions-table"
            // Head cells inherit the dialog's own surface, so rows passing under
            // them are hidden rather than showing through.
            sx={{ '& .MuiTableCell-stickyHeader': { backgroundColor: 'background.paper' } }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map(column => (
                  <TableCell
                    key={column.key}
                    align={column.align}
                    sortDirection={sortKey === column.key ? sortDirection : false}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={column.align === 'right' ? 'flex-end' : 'flex-start'}>
                      <TableSortLabel
                        active={sortKey === column.key}
                        direction={sortKey === column.key ? sortDirection : 'asc'}
                        onClick={() => requestSort(column.key)}
                        data-testid={`closed-transactions-sort-${column.key}`}>
                        {column.label}
                      </TableSortLabel>
                      {columnFilter(column.key)}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleGroups.map((group, index) => {
                const key = closedTransactionGroupKey(group);
                const excluded = excludedKeys.has(key);
                const highlightSx = {
                  color: group.since_last_valuation ? 'error.main' : 'inherit',
                  fontWeight: group.since_last_valuation ? 700 : 400,
                };
                return (
                  <TableRow
                    key={key}
                    hover
                    data-testid={`closed-transactions-row-${index}`}
                    data-since-last-valuation={group.since_last_valuation ? 'true' : undefined}
                    data-excluded={excluded ? 'true' : undefined}
                    // The checkbox is revealed on hover and on focus within the
                    // row, the way GPC reveals it on cell selection. An already
                    // excluded row keeps it visible, so the strike-through is
                    // never unexplained.
                    sx={{ '&:hover .closed-transactions-include, &:focus-within .closed-transactions-include': { opacity: 1 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={highlightSx}>
                        {formatClosingDate(group.transaction_date)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        data-testid={`closed-transactions-share-class-${index}`}
                        sx={highlightSx}>
                        {group.security_type || EM_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        data-testid={`closed-transactions-structure-${index}`}
                        sx={highlightSx}>
                        {group.structure || EM_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        <Typography
                          variant="body2"
                          data-testid={`closed-transactions-price-${index}`}
                          sx={{ ...highlightSx, textDecoration: excluded ? 'line-through' : 'none' }}>
                          {formatPrice(group.price_per_share)}
                        </Typography>
                        <Checkbox
                          size="small"
                          checked={!excluded}
                          onChange={() => toggleExcluded(group)}
                          className="closed-transactions-include"
                          data-testid={`closed-transactions-include-${index}`}
                          inputProps={{ 'aria-label': `Include ${formatPrice(group.price_per_share)} in the averages` }}
                          sx={{ opacity: excluded ? 1 : 0, p: 0.25, transition: 'opacity 120ms' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        data-testid={`closed-transactions-size-${index}`}
                        data-exact-volume={group.has_exact_volume ? 'true' : undefined}
                        sx={highlightSx}>
                        {group.volume_label || EM_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        data-testid={`closed-transactions-shares-${index}`}
                        sx={highlightSx}>
                        {group.shares_label || EM_DASH}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatClosedTransactionSources(group)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      {visibleGroups.length > 0 && (
        <Box
          px={3}
          py={1.5}
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={3}
          flexShrink={0}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          data-testid="closed-transactions-stats">
          <Typography variant="body2" fontWeight={700}>
              Volume Weighted Average Price
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.25}>
            <StatRow
              label="Low"
              value={stats.low}
              definition={definitions.low}
              testId="closed-transactions-stat-low"
            />
            <StatRow
              label="Average"
              value={stats.average}
              definition={definitions.average}
              testId="closed-transactions-stat-average"
            />
            <StatRow
              label="Median"
              value={stats.median}
              definition={definitions.median}
              testId="closed-transactions-stat-median"
            />
            <StatRow
              label="High"
              value={stats.high}
              definition={definitions.high}
              testId="closed-transactions-stat-high"
            />
          </Box>
        </Box>
      )}
      <DialogActions>
        <Button onClick={onClose} id="closed-transactions-close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosedTransactionsDialog;
